import React, { useEffect, useState } from 'react';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Row,
  Col,
} from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { personnelAPI } from '../../helpers/api';

interface PersonnelFormModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSuccess: () => void;
}

// Default title options (fallback if no titles in DB)
const DEFAULT_TITLES = [
  'İşçi',
  'Kalfa',
  'Usta',
  'Operatör',
  'Elektrikçi',
  'Tesisatçı',
  'Demir Ustası',
  'Kaynakçı',
];

// Telefon numarasını TR formatına çevir: 533 123 12 23
const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  let cleaned = digits;
  if (cleaned.startsWith('90') && cleaned.length > 10) cleaned = cleaned.slice(2);
  if (cleaned.startsWith('0') && cleaned.length > 10) cleaned = cleaned.slice(1);
  cleaned = cleaned.slice(0, 10);

  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
  if (cleaned.length <= 8) return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8)}`;
};

const unformatPhone = (value: string): string => value.replace(/\D/g, '');

const PersonnelFormModal: React.FC<PersonnelFormModalProps> = ({
  isOpen,
  toggle,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    tcNo: '',
    phone: '',
    title: '',
    customTitle: '',
    dailyWage: '',
    monthlySalary: '',
    startDate: new Date().toISOString().split('T')[0],
    type: 'COMPANY',
    employmentType: 'DAILY',
  });

  // Titles for dropdown
  const [titles, setTitles] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      resetForm();
      fetchTitles();
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      tcNo: '',
      phone: '',
      title: '',
      customTitle: '',
      dailyWage: '',
      monthlySalary: '',
      startDate: new Date().toISOString().split('T')[0],
      type: 'COMPANY',
      employmentType: 'DAILY',
    });
  };

  const fetchTitles = async () => {
    try {
      const response = await personnelAPI.getTitles();
      const dbTitles = response.data as string[];
      // Merge DB titles with defaults, removing duplicates
      const allTitles = [...new Set([...dbTitles, ...DEFAULT_TITLES])].sort();
      setTitles(allTitles);
    } catch (error) {
      console.error('Titles fetch error:', error);
      setTitles(DEFAULT_TITLES);
    }
  };

  // Get actual title value (from dropdown or custom input)
  const getActualTitle = () => formData.title === '__OTHER__' ? formData.customTitle : formData.title;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName) {
      toast.error('Ad ve soyad zorunludur');
      return;
    }

    try {
      setSubmitting(true);
      const actualTitle = getActualTitle();

      await personnelAPI.create({
        firstName: formData.firstName,
        lastName: formData.lastName,
        tcNo: formData.tcNo || undefined,
        phone: formData.phone ? unformatPhone(formData.phone) : undefined,
        title: actualTitle || undefined,
        dailyWage: formData.employmentType === 'DAILY' && formData.dailyWage ? parseFloat(formData.dailyWage) : undefined,
        monthlySalary: formData.employmentType === 'MONTHLY' && formData.monthlySalary ? parseFloat(formData.monthlySalary) : undefined,
        startDate: formData.startDate || undefined,
        type: formData.type,
        employmentType: formData.employmentType,
      });
      toast.success('Personel eklendi');
      onSuccess();
      toggle();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Yeni Personel"
      size="md"
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        <Form onSubmit={handleSubmit} className="personnel-form">
          {/* Kişisel Bilgiler Card */}
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
            <h6 className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-user-3-line me-2"></i>
              Kişisel Bilgiler
            </h6>
            <div>
            <Row>
              <Col xs={6}>
                <FormGroup>
                  <Label>Ad *</Label>
                  <Input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    placeholder="Adı girin"
                    required
                  />
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup>
                  <Label>Soyad *</Label>
                  <Input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    placeholder="Soyadı girin"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>TC Kimlik No</Label>
              <Input
                type="text"
                value={formData.tcNo}
                onChange={(e) =>
                  setFormData({ ...formData, tcNo: e.target.value.replace(/\D/g, '').slice(0, 11) })
                }
                placeholder="11 haneli TC No"
                maxLength={11}
              />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label>Telefon</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: formatPhone(e.target.value) })
                }
                placeholder="5XX XXX XX XX"
              />
            </FormGroup>
            </div>
          </div>

          {/* İş Bilgileri Card */}
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
            <h6 className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-briefcase-line me-2"></i>
              İş Bilgileri
            </h6>
            <div>
            <FormGroup>
              <Label>Ünvan / Pozisyon</Label>
              <Input
                type="select"
                value={formData.title}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({
                    ...formData,
                    title: value,
                    customTitle: value === '__OTHER__' ? formData.customTitle : '',
                  });
                }}
              >
                <option value="">Seçin</option>
                {titles.map((title) => (
                  <option key={title} value={title}>{title}</option>
                ))}
                <option value="__OTHER__">+ Diğer (Yeni Ünvan)</option>
              </Input>
              {formData.title === '__OTHER__' && (
                <Input
                  type="text"
                  value={formData.customTitle}
                  onChange={(e) => setFormData({ ...formData, customTitle: e.target.value })}
                  placeholder="Yeni ünvan girin"
                  className="mt-2"
                />
              )}
            </FormGroup>
            <Row>
              <Col xs={6}>
                <FormGroup>
                  <Label>Personel Türü</Label>
                  <Input
                    type="select"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="COMPANY">Şirket</option>
                    <option value="SUBCONTRACTOR">Taşeron</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup>
                  <Label>Çalışma Tipi</Label>
                  <Input
                    type="select"
                    value={formData.employmentType}
                    onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
                  >
                    <option value="DAILY">Yevmiyeci</option>
                    <option value="MONTHLY">Aylıkçı</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <FormGroup className="mb-0">
                  {formData.employmentType === 'DAILY' ? (
                    <>
                      <Label>Günlük Ücret (TL)</Label>
                      <Input
                        type="number"
                        value={formData.dailyWage}
                        onChange={(e) =>
                          setFormData({ ...formData, dailyWage: e.target.value })
                        }
                        placeholder="Günlük yevmiye"
                        min={0}
                      />
                    </>
                  ) : (
                    <>
                      <Label>Aylık Maaş (TL)</Label>
                      <Input
                        type="number"
                        value={formData.monthlySalary}
                        onChange={(e) =>
                          setFormData({ ...formData, monthlySalary: e.target.value })
                        }
                        placeholder="Net maaş"
                        min={0}
                      />
                    </>
                  )}
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup className="mb-0">
                  <Label>İşe Başlama Tarihi</Label>
                  <Input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) =>
                      setFormData({ ...formData, startDate: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
            </div>
          </div>

          {/* Kaydet Butonu */}
          <div className="form-actions">
            <Button color="secondary" onClick={toggle} disabled={submitting}>
              İptal
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <i className="ri-save-line me-1"></i>
                  Kaydet
                </>
              )}
            </Button>
          </div>
        </Form>
      </div>
    </SideModal>
  );
};

export default PersonnelFormModal;
